import { Flex, Icon, Text, cn } from '@kognitos/lattice';

import type { IRainAgentIdentity } from '../utils/agent-identity';

import { RainAgentChip } from './RainAgentChip';

/** `returned`: the agent handed the work back; the return leg's own row says it finished. */
export type TRainTransferStatus =
  | 'pending'
  | 'active'
  | 'returned'
  | 'finished'
  | 'failed';

export interface IRainTransferMarkerProps {
  /** The agent that did the work. */
  agent: IRainAgentIdentity;
  status: TRainTransferStatus;
  /**
   * Text that completes the sentence started by the chip (e.g. "picked this
   * up", so it reads "Browser agent picked this up"). Built by the caller so
   * i18n lives in one place.
   */
  label: string;
  className?: string;
}

/**
 * Status row for each leg of a handoff: `[agent] picked this up` when it
 * starts, `[agent] finished its work in 12s` when control comes back. Both
 * name the agent that did the work, never the one that handed it off.
 */
export function RainTransferMarker({
  agent,
  status,
  label,
  className,
}: IRainTransferMarkerProps) {
  const isFailed = status === 'failed';
  const isWorking = status === 'pending' || status === 'active';

  return (
    <Flex
      align="center"
      wrap="wrap"
      gap={1.5}
      className={cn('w-full min-w-0 py-1', className)}
      data-testid="rain-transfer-marker"
      data-status={status}
    >
      <RainAgentChip identity={agent} />
      {/* Sized to its text, so on a narrow screen it wraps below the chip as one unit. */}
      <Flex align="center" gap={1.5} className="min-w-0">
        <Text
          level="small"
          color={isFailed ? 'destructive' : 'muted'}
          className="min-w-0 break-words"
          data-testid="rain-transfer-label"
        >
          {label}
        </Text>
        {isWorking && (
          <Icon
            type="LoaderCircle"
            size="sm"
            className="shrink-0 animate-spin text-muted-foreground"
            aria-hidden
          />
        )}
        {status === 'finished' && (
          <Icon
            type="CircleCheck"
            size="sm"
            className="shrink-0 text-muted-foreground"
            aria-hidden
          />
        )}
        {isFailed && (
          <Icon
            type="AlertCircle"
            size="sm"
            className="shrink-0 text-destructive"
            aria-hidden
          />
        )}
      </Flex>
    </Flex>
  );
}
